import React, { useState } from 'react'
import {
    AreaConfig,
    Lease,
    LeaseStatus,
    LeaseStatusType,
    ModelMap,
    Unit,
    getInspectionStatusLabel,
    getLeaseStatusString,
    getLeaseTransition,
    leaseDisplayedPets,
    leaseMoveInDiffersFromStart,
    leaseMoveOutDiffersFromEnd,
} from '../../../../models'
import {
    AvatarCircle,
    ConfirmationButton,
    Container,
    EllipsisSpan,
    LeasePetMarker,
} from '../../../../components'
import { toMMDDYYYY } from '../../../../helpers'
import {
    Checkbox,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    makeStyles,
} from '@material-ui/core'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import EditIcon from '@material-ui/icons/Edit'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import { ReactComponent as ToolSBIcon } from '../../../../assets/icons/tool.svg'
import { toast } from 'react-toastify'
import { leaseCanTransfer, leasePlaceLabels } from './leaseBoard'

const useStyles = makeStyles((theme: Theme) => ({
    row: {
        cursor: 'pointer',
    },
    cell: {
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    placeCell: {
        whiteSpace: 'normal',
        minWidth: 110,
        maxWidth: 200,
        lineHeight: 1.35,
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 180,
        maxWidth: 240,
    },
    identityText: {
        flexDirection: 'column',
        marginLeft: theme.spacing(1.5),
        justifyContent: 'center',
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
    },
    name: {
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.3,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    email: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    moveInValue: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    doorIcon: {
        width: 16,
        height: 16,
        flexShrink: 0,
    },
    doorIconSame: {
        color: '#8A8A8A',
    },
    doorIconDifferent: {
        color: '#008C85',
    },
    unitSub: {
        display: 'block',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        justifyContent: 'flex-end',
    },
    iconGrid: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    iconSlot: {
        width: 20,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    attributeIcon: {
        width: 16,
        height: 16,
        display: 'block',
        flexShrink: 0,
    },
    damageButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        cursor: 'pointer',
        lineHeight: 0,
    },
    statusLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
}))

function statusChipStyle(status: LeaseStatusType): React.CSSProperties {
    const palette: Record<
        LeaseStatusType,
        { backgroundColor: string; color: string }
    > = {
        [LeaseStatus.APPLICANT]: {
            backgroundColor: '#f3f4f6',
            color: '#374151',
        },
        [LeaseStatus.CANCELLED]: {
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
        },
        [LeaseStatus.FUTURE]: {
            backgroundColor: '#dbeafe',
            color: '#1d4ed8',
        },
        [LeaseStatus.CURRENT]: {
            backgroundColor: '#d1fae5',
            color: '#047857',
        },
        [LeaseStatus.NOTICE]: {
            backgroundColor: '#ffedd5',
            color: '#c2410c',
        },
        [LeaseStatus.PAST]: {
            backgroundColor: '#e5e7eb',
            color: '#4b5563',
        },
    }
    return {
        ...palette[status],
        fontWeight: 700,
        fontSize: '0.6875rem',
        height: 22,
        borderRadius: 6,
    }
}

export const LeaseRow = (props: {
    lease: Lease
    unitMap: ModelMap<Unit>
    areaConfigMap: ModelMap<AreaConfig>
    transitionLease: (leaseId: number, toStatus: LeaseStatusType) => void
    onClick: (lease: Lease) => void
    allowTransitions: boolean
    isChecked: boolean
    onCheck: () => void
    onClickTransfer: () => void
    selectionMode?: boolean
    isAccountManager?: boolean
}) => {
    const {
        lease,
        unitMap,
        areaConfigMap,
        transitionLease,
        onClick,
        isAccountManager,
        allowTransitions,
        isChecked,
        onCheck,
    } = props

    const classes = useStyles()

    const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
        null,
    )

    const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isAccountManager) {
            event.stopPropagation()
            setStatusAnchorEl(event.currentTarget)
        }
    }

    const handleStatusClose = () => {
        setStatusAnchorEl(null)
    }

    const handleStatusChange = (newStatus: LeaseStatusType) => {
        transitionLease(lease.id, newStatus)
        handleStatusClose()
    }

    const unit: Unit | undefined = unitMap[lease.area.unit]

    const areaConfig: AreaConfig | undefined =
        areaConfigMap[lease.area.area_config]

    const leaseTransition = getLeaseTransition(lease.status)

    const today = new Date()
    const leaseEndDate = new Date(lease.end_date)
    const moveInDiffers = leaseMoveInDiffersFromStart(lease)
    const moveOutDiffers = leaseMoveOutDiffersFromEnd(lease)
    const moveInDate = lease.move_in_date || lease.start_date
    const moveOutDate = lease.move_out_date || lease.end_date
    const moveInStatusLabel = getInspectionStatusLabel(
        lease.tenant_move_in_inspection?.status,
    )

    const place = leasePlaceLabels(lease, unit, areaConfig?.name)
    const canTransfer = leaseCanTransfer(lease)
    const petDisplay = leaseDisplayedPets(lease)

    let toolTipText: string | JSX.Element = 'Transfer'

    if (!canTransfer) {
        toolTipText = (
            <Container style={{ flexDirection: 'column' }}>
                {leaseEndDate < today && (
                    <span>- Lease end date has already passed</span>
                )}
                {lease.status === LeaseStatus.PAST && (
                    <span>- Lease has already been set to Past</span>
                )}
            </Container>
        )
    }

    const doorTooltip = moveInDiffers
        ? `Move-in differs from lease start ${toMMDDYYYY(
              new Date(lease.start_date),
          )}`
        : 'Same as lease start'
    const exitTooltip = moveOutDiffers
        ? `Move-out differs from lease end ${toMMDDYYYY(
              new Date(lease.end_date),
          )}`
        : 'Same as lease end'

    return (
        <TableRow hover className={classes.row} onClick={() => onClick(lease)}>
            <TableCell className={classes.cell}>
                <div className={classes.identity}>
                    <AvatarCircle
                        text={lease.user.name}
                        initials
                        styles={{
                            container: {
                                minHeight: 36,
                                maxHeight: 36,
                                minWidth: 36,
                                maxWidth: 36,
                                fontSize: 13,
                            },
                        }}
                    />
                    <Container className={classes.identityText}>
                        <EllipsisSpan className={classes.name}>
                            {lease.user.name}
                        </EllipsisSpan>
                        <EllipsisSpan className={classes.email}>
                            {lease.user.email}
                        </EllipsisSpan>
                    </Container>
                </div>
            </TableCell>
            <TableCell className={classes.cell}>
                <div className={classes.iconGrid}>
                    <div className={classes.iconSlot}>
                        <LeasePetMarker
                            pets={petDisplay.pets}
                            hasPet={petDisplay.hasPet}
                            style={{
                                width: 16,
                                height: 16,
                            }}
                        />
                    </div>
                    <div className={classes.iconSlot}>
                        {lease.latest_damage_report && (
                            <Tooltip title="View Damage Report">
                                <span
                                    className={classes.damageButton}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        window.open(
                                            lease.latest_damage_report?.file,
                                            '_blank',
                                        )
                                    }}
                                >
                                    <ToolSBIcon
                                        className={classes.attributeIcon}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </TableCell>
            <TableCell className={`${classes.cell} ${classes.placeCell}`}>
                {place.location}
            </TableCell>
            <TableCell className={`${classes.cell} ${classes.placeCell}`}>
                <span>{place.unitName}</span>
                <span className={classes.unitSub}>{place.bed}</span>
            </TableCell>
            <TableCell className={classes.cell}>
                {toMMDDYYYY(new Date(lease.start_date))}
            </TableCell>
            <TableCell className={classes.cell}>
                <span className={classes.moveInValue}>
                    <Tooltip title={doorTooltip}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <MeetingRoomIcon
                                className={`${classes.doorIcon} ${
                                    moveInDiffers
                                        ? classes.doorIconDifferent
                                        : classes.doorIconSame
                                }`}
                            />
                        </span>
                    </Tooltip>
                    {toMMDDYYYY(new Date(moveInDate))}
                </span>
            </TableCell>
            <TableCell className={classes.cell}>
                {toMMDDYYYY(new Date(lease.end_date))}
            </TableCell>
            <TableCell className={classes.cell}>
                <span className={classes.moveInValue}>
                    <Tooltip title={exitTooltip}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <ExitToAppIcon
                                className={`${classes.doorIcon} ${
                                    moveOutDiffers
                                        ? classes.doorIconDifferent
                                        : classes.doorIconSame
                                }`}
                            />
                        </span>
                    </Tooltip>
                    {toMMDDYYYY(new Date(moveOutDate))}
                </span>
            </TableCell>
            <TableCell className={classes.cell}>
                <Chip
                    size="small"
                    style={{
                        ...statusChipStyle(lease.status),
                        cursor: isAccountManager ? 'pointer' : 'default',
                    }}
                    onClick={handleStatusClick}
                    label={
                        <span className={classes.statusLabel}>
                            {getLeaseStatusString(lease.status)}
                            {isAccountManager && (
                                <EditIcon style={{ fontSize: 14 }} />
                            )}
                        </span>
                    }
                />
                <Menu
                    anchorEl={statusAnchorEl}
                    open={Boolean(statusAnchorEl)}
                    onClose={handleStatusClose}
                    onClick={(e) => e.stopPropagation()}
                >
                    {Object.values(LeaseStatus).map((status) => (
                        <MenuItem
                            key={status}
                            onClick={() => handleStatusChange(status)}
                            selected={status === lease.status}
                        >
                            {getLeaseStatusString(status)}
                        </MenuItem>
                    ))}
                </Menu>
            </TableCell>
            <TableCell className={classes.cell}>
                {moveInStatusLabel || '—'}
            </TableCell>
            <TableCell className={classes.cell} align="right">
                <div className={classes.actions}>
                    <div onClick={(e) => e.stopPropagation()}>
                        <ConfirmationButton
                            actionLabel={
                                leaseTransition?.positive?.actionLabel ?? '?'
                            }
                            onConfirm={() => {
                                if (leaseTransition?.positive?.status) {
                                    transitionLease(
                                        lease.id,
                                        leaseTransition.positive.status,
                                    )
                                } else {
                                    toast.error(
                                        'The lease cannot be transitioned forward.',
                                    )
                                }
                            }}
                        />
                    </div>

                    <Tooltip title={toolTipText}>
                        <span>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation()
                                    props.onClickTransfer()
                                }}
                                disabled={!canTransfer}
                                size="small"
                            >
                                <SwapHorizIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {allowTransitions && (
                        <Checkbox
                            checked={isChecked}
                            onChange={onCheck}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            </TableCell>
        </TableRow>
    )
}
